import { useState } from 'react'
import { fbt } from 'fbtee'
import { SCHEDULE_MARKER_LABEL_MAX } from '@shared/firestore'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import { AudiencePicker } from '../../../components/AudiencePicker'
import { useAudienceSelection } from '../../../hooks/useAudienceSelection'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'
import { cancelButtonLabel, saveButtonLabel } from '../../../lib/commonStrings'
import type { MarkerInput } from '../../../hooks/useScheduleMarkers'
import type { Group, ScheduleMarker } from '../../../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Groups the viewer is a member of — the audience channels besides Friends. */
  groups: Group[]
  /** The line being edited; absent when creating. */
  marker?: ScheduleMarker
  defaultTime: string
  onSave: (input: MarkerInput) => Promise<void>
  onDelete: () => Promise<void>
}

/** Add or edit one of the viewer's schedule lines: label, time and who sees it. */
export function ScheduleMarkerDialog({ open, onOpenChange, marker, ...form }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* No focus return on close: the opener is a grid pin, and returning focus
          to it lands outside a dialog reopened before the close animation ends. */}
      <DialogContent className="max-w-sm" onCloseAutoFocus={e => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {marker
              ? fbt('Edit line', 'Title of the dialog that edits a custom line on the schedule')
              : fbt('Add a line', 'Title of the dialog that adds a custom labeled line to the schedule')}
          </DialogTitle>
          <DialogDescription>
            {fbt(
              'Mark a moment on your schedule.',
              'Subtitle of the dialog that adds or edits a custom line on the schedule',
            )}
          </DialogDescription>
        </DialogHeader>
        {open && <MarkerForm {...form} marker={marker} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function MarkerForm({ groups, marker, defaultTime, onSave, onDelete, onClose }: Omit<Props, 'open' | 'onOpenChange'> & {
  onClose: () => void
}) {
  const cutoff = useDayCutoff()
  const [label, setLabel] = useState(marker?.label ?? '')
  const [time, setTime] = useState(marker?.time ?? defaultTime)
  const [onlyMe, setOnlyMe] = useState(marker ? marker.isPrivate : true)
  const audience = useAudienceSelection(groups, marker && !marker.isPrivate ? marker : undefined)
  const [submitting, setSubmitting] = useState(false)

  // Unticking every channel leaves nobody to share with — that's "only me".
  const isPrivate = onlyMe || audience.count === 0
  const canSave = !submitting && label.trim().length > 0 && /^\d{2}:\d{2}$/.test(time)
  const afterMidnight = /^\d{2}:\d{2}$/.test(time) && Number(time.slice(0, 2)) < cutoff

  async function run(action: () => Promise<void>) {
    setSubmitting(true)
    try {
      await action()
      onClose()
    } catch {
      // runMutation already showed the error toast; keep the dialog open.
    } finally {
      setSubmitting(false)
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSave) return
    void run(() =>
      onSave({
        time,
        label: label.trim(),
        audience: isPrivate ? { isPrivate: true } : { isPrivate: false, ...audience.value },
      }),
    )
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="marker-label">{fbt('Label', 'Field label for the text of a custom schedule line')}</Label>
        <Input
          id="marker-label"
          data-testid="marker-label"
          value={label}
          onChange={e => setLabel(e.target.value)}
          maxLength={SCHEDULE_MARKER_LABEL_MAX}
          autoFocus
          placeholder={fbt('Meet at the river, back to camp…','Placeholder of the label field for a custom schedule line')}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="marker-time">{fbt('Time', 'Field label for the time of a custom schedule line')}</Label>
        <Input
          id="marker-time"
          data-testid="marker-time"
          type="time"
          step={300}
          value={time}
          onChange={e => setTime(e.target.value)}
        />
        {afterMidnight && (
          <p className="text-xs text-muted-foreground">
            {fbt(
              "After midnight, so it's placed at the end of this day's night.",
              'Hint under the time of a custom schedule line when the time falls after midnight',
            )}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {fbt('Visible to', 'Label before the audience picker of a custom schedule line')}
        </span>
        <AudiencePicker
          groups={groups}
          selection={audience}
          onlyMe={{
            active: isPrivate,
            label: fbt('Only me', 'Audience option that keeps a custom schedule line private').toString(),
            onChange: setOnlyMe,
          }}
          triggerLabel={
            isPrivate
              ? fbt('Only me', 'Schedule line audience picker label when the line is private')
              : audience.isEveryone
                ? fbt('All your crew', 'Schedule line audience picker label when every group and friends see it')
                : fbt(
                    fbt.plural('channel', audience.count, { showCount: 'yes', many: 'channels' }),
                    'Schedule line audience picker label showing how many groups/friends see it',
                  )
          }
          header={fbt('Who sees this line?', 'Header of the audience picker of a custom schedule line')}
          friendsLabel={fbt('Friends', 'Audience option to show a custom schedule line to your friends').toString()}
        />
      </div>
      <div className="flex items-center gap-2">
        {marker && (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto text-destructive"
            disabled={submitting}
            onClick={() => void run(onDelete)}
          >
            {fbt('Delete', 'Button in the schedule line dialog that deletes the line')}
          </Button>
        )}
        <Button type="button" variant="outline" className={marker ? undefined : 'ml-auto'} onClick={onClose}>
          {cancelButtonLabel()}
        </Button>
        <Button type="submit" disabled={!canSave}>
          {saveButtonLabel()}
        </Button>
      </div>
    </form>
  )
}
